import { PefData, PefSheet, resolvePefCell } from './pef-schema.types';

// ---------------------------------------------------------------------------
// Autofill event types
// ---------------------------------------------------------------------------

export type PefAutofillType =
  | 'column-to-all'
  | 'column-to-column'
  | 'row-fill'
  | 'row-progressive';

/** How a row-progressive increment is applied at each step. */
export type PefIncrementMode = 'percentage' | 'absolute';

export interface PefAutofillEvent {
  type: PefAutofillType;
  sheetId: string;
  /** Source column ID — always required */
  sourceColumnId: string;
  /** Only for column-to-column: the target column */
  targetColumnId?: string;
  /** Only for row-fill / row-progressive: the row to spread across all columns */
  rowId?: string;
  /**
   * Only for row-progressive: the column to start the progression from.
   * Its value becomes the base; earlier columns are left untouched.
   * When omitted, the first column holding a usable value is used.
   */
  startColumnId?: string;
  /** Only for row-progressive: number of columns between each increment step (>= 1) */
  interval?: number;
  /** Only for row-progressive: the increment amount applied at each step */
  increment?: number;
  /** Only for row-progressive: whether the increment is a percentage or a fixed amount */
  incrementMode?: PefIncrementMode;
}

// ---------------------------------------------------------------------------
// Autofill computation
// ---------------------------------------------------------------------------

/**
 * Computes the set of PefData key/value updates that an autofill action
 * should apply, without mutating any state.
 *
 * Returns an empty object when the event references an unknown row/column or
 * when no input cells are found in the affected region.
 */
export function computeAutofillUpdates(
  event: PefAutofillEvent,
  sheet: PefSheet,
  data: PefData,
): PefData {
  const updates: PefData = {};

  if (event.type === 'column-to-all' || event.type === 'column-to-column') {
    const targetCols =
      event.type === 'column-to-all'
        ? sheet.columns.filter(
            (c) => c.id !== event.sourceColumnId && !c.standalone,
          )
        : sheet.columns.filter((c) => c.id === event.targetColumnId);

    for (const row of sheet.rows) {
      const sourceCell = resolvePefCell(sheet, row, event.sourceColumnId);
      if (sourceCell.type !== 'input') continue;
      const sourceKey =
        sourceCell.valueKey ?? `${sheet.id}.${row.id}.${event.sourceColumnId}`;
      const sourceValue = data[sourceKey] ?? null;

      for (const targetCol of targetCols) {
        const targetCell = resolvePefCell(sheet, row, targetCol.id);
        if (targetCell.type !== 'input') continue;
        const targetKey =
          targetCell.valueKey ?? `${sheet.id}.${row.id}.${targetCol.id}`;
        updates[targetKey] = sourceValue;
      }
    }
  } else if (event.type === 'row-fill') {
    const row = sheet.rows.find((r) => r.id === event.rowId);
    if (!row) return updates;

    const inputCols = sheet.columns.filter(
      (c) => resolvePefCell(sheet, row, c.id).type === 'input',
    );

    let fillValue: number | string | null = null;
    for (const col of inputCols) {
      const cell = resolvePefCell(sheet, row, col.id);
      const key = cell.valueKey ?? `${sheet.id}.${row.id}.${col.id}`;
      const val = data[key];
      if (val !== null && val !== undefined) {
        fillValue = val as number | string;
        break;
      }
    }

    for (const col of inputCols) {
      const cell = resolvePefCell(sheet, row, col.id);
      const key = cell.valueKey ?? `${sheet.id}.${row.id}.${col.id}`;
      updates[key] = fillValue;
    }
  } else if (event.type === 'row-progressive') {
    const row = sheet.rows.find((r) => r.id === event.rowId);
    if (!row) return updates;

    const interval = Math.max(1, Math.floor(event.interval ?? 1));
    const increment = event.increment ?? 0;
    const mode = event.incrementMode ?? 'absolute';

    const inputCols = sheet.columns.filter(
      (c) => resolvePefCell(sheet, row, c.id).type === 'input',
    );

    const readNumeric = (idx: number): number | null => {
      const cell = resolvePefCell(sheet, row, inputCols[idx].id);
      const key = cell.valueKey ?? `${sheet.id}.${row.id}.${inputCols[idx].id}`;
      const val = data[key];
      if (val === null || val === undefined || val === '') return null;
      const num = typeof val === 'number' ? val : parseFloat(String(val));
      return isNaN(num) ? null : num;
    };

    // Base column: the explicit start column when given, otherwise the first
    // column that holds a usable numeric value. Earlier columns stay untouched.
    let baseIdx = -1;
    let baseValue = 0;
    if (event.startColumnId) {
      const idx = inputCols.findIndex((c) => c.id === event.startColumnId);
      const num = idx === -1 ? null : readNumeric(idx);
      if (num !== null) {
        baseIdx = idx;
        baseValue = num;
      }
    } else {
      for (let i = 0; i < inputCols.length; i++) {
        const num = readNumeric(i);
        if (num !== null) {
          baseIdx = i;
          baseValue = num;
          break;
        }
      }
    }
    if (baseIdx === -1) return updates;

    for (let i = baseIdx; i < inputCols.length; i++) {
      const cell = resolvePefCell(sheet, row, inputCols[i].id);
      const key = cell.valueKey ?? `${sheet.id}.${row.id}.${inputCols[i].id}`;
      // Step-hold: value is flat within each interval and jumps at boundaries.
      const steps = Math.floor((i - baseIdx) / interval);
      const raw =
        mode === 'percentage'
          ? baseValue * Math.pow(1 + increment / 100, steps)
          : baseValue + increment * steps;
      // Round to 2 decimals to avoid floating-point noise (e.g. 110.25000001).
      updates[key] = Math.round(raw * 100) / 100;
    }
  }

  return updates;
}
